import { Preferences } from '@capacitor/preferences';
import { isNative } from '@/lib/platform';

/**
 * Auth-token storage abstraction.
 *
 * Web: backed by localStorage (when remember-me) or sessionStorage (don't
 * remember me). Behavior unchanged from before — this is a thin async wrapper
 * over the existing two-storage pattern.
 *
 * Native: writes mirror to @capacitor/preferences (iOS Keychain on iOS,
 * EncryptedSharedPreferences on Android). Reads prefer Preferences over
 * localStorage to survive WKWebView eviction under memory pressure
 * (spec §M1 motivation; iOS-only concern, but the code path is symmetric
 * across platforms).
 *
 * Spike (Android API 34): vanilla localStorage already survived force-quit
 * and `adb install -r`. The Preferences mirror is primarily an iOS safeguard;
 * Android cost is negligible.
 *
 * Why async: Preferences API is Promise-based on native; localStorage is sync
 * on web. The abstraction returns Promises everywhere for a single signature.
 */

// 'trusted_device_token' (#763) is NATIVE-only state: web carries it as an
// httpOnly cookie the server manages. It deliberately survives logout — the
// whole point is skipping the next login's MFA challenge on this device.
const TOKEN_KEYS = ['auth_token', 'refresh_token', 'trusted_device_token'] as const;
export type AuthKey = (typeof TOKEN_KEYS)[number];

export interface AuthStorageWriteOptions {
  /** true → persist across browser restart (localStorage); false → session only (sessionStorage) */
  remember: boolean;
}

/**
 * Web-only: the access token lives in JS memory, not web storage. This removes
 * the XSS exposure of a JS-readable token across reloads — the page bootstraps
 * a fresh one from the httpOnly refresh cookie via /auth/refresh on boot. The
 * refresh token itself is never JS-visible on web (it's the httpOnly cookie),
 * so it has no in-memory counterpart. Native is unchanged: both tokens live in
 * @capacitor/preferences + the legacy web storages.
 */
let inMemoryAccessToken: string | null = null;

export const authStorage = {
  /**
   * Read a token. On native: try Preferences first (durable source), fall back
   * to web storages (handles the first read before any write has populated
   * Preferences). On web: the access token comes from the in-memory variable;
   * the refresh token is the httpOnly cookie and is invisible to JS, so it
   * always reads as null.
   */
  async get(key: AuthKey): Promise<string | null> {
    if (isNative()) {
      const { value } = await Preferences.get({ key });
      if (value != null) return value;
      return localStorage.getItem(key) ?? sessionStorage.getItem(key);
    }
    // Web
    if (key === 'auth_token') return inMemoryAccessToken;
    return null; // refresh_token is the httpOnly cookie — not JS-readable
  },

  /**
   * Write a token. On native: web target selected by opts.remember (matches
   * legacy remember-me semantics) plus a Preferences mirror; invariant — the
   * value lives in exactly one web storage at a time. On web: the access token
   * goes to the in-memory variable (NOT web storage, so it can't be read by
   * XSS or survive a reload); the refresh token is server-managed via the
   * httpOnly cookie, so writes are a no-op.
   */
  async set(key: AuthKey, value: string, opts: AuthStorageWriteOptions): Promise<void> {
    if (isNative()) {
      const target = opts.remember ? localStorage : sessionStorage;
      const other = opts.remember ? sessionStorage : localStorage;
      target.setItem(key, value);
      other.removeItem(key);
      await Preferences.set({ key, value });
      return;
    }
    // Web
    if (key === 'auth_token') inMemoryAccessToken = value;
    // refresh_token: no-op — the cookie is set/rotated by the server.
  },

  /**
   * Remove a single key. On native: from both web storages and Preferences. On
   * web: null out the in-memory access token AND clear web storage (harmless,
   * and purges any legacy tokens left over from before the in-memory change).
   */
  async clear(key: AuthKey): Promise<void> {
    if (!isNative() && key === 'auth_token') inMemoryAccessToken = null;
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
    if (isNative()) {
      await Preferences.remove({ key });
    }
  },

  /** Clear the SESSION tokens (auth + refresh) from every backing store.
   *  Deliberately leaves 'trusted_device_token' alone: it is device state,
   *  not session state — surviving logout is its entire purpose (#763), and
   *  server-side revocation on password change is what actually kills it. */
  async clearAll(): Promise<void> {
    if (!isNative()) inMemoryAccessToken = null;
    for (const k of TOKEN_KEYS) {
      if (k === 'trusted_device_token') continue;
      await this.clear(k);
    }
  },

  /**
   * Detect whether the current logged-in session is "remember me". On native,
   * checked by which web storage holds the auth_token. On web, the access token
   * lives only in memory, so decode its `rem` claim instead (no signature
   * verification — `rem` is a UI hint, not a security decision).
   */
  /**
   * Synchronous read of the access token for callers that build headers at
   * call time and cannot await (the chat-core endpoint adapters, #1731). Web:
   * the in-memory token. Native: the web-storage mirror, which `set` keeps in
   * step with Preferences. Never the refresh token.
   */
  peekAccessToken(): string | null {
    if (!isNative()) return inMemoryAccessToken;
    return localStorage.getItem('auth_token') ?? sessionStorage.getItem('auth_token');
  },

  isRemembered(): boolean {
    if (!isNative()) {
      if (!inMemoryAccessToken) return false;
      try {
        const payload = JSON.parse(atob(inMemoryAccessToken.split('.')[1]));
        return payload.rem === true;
      } catch {
        return false;
      }
    }
    return localStorage.getItem('auth_token') !== null;
  },
};
